/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createAsyncThunk } from "@reduxjs/toolkit";
import { OptionType } from "@tivocorp/data-domain-lib/lib/client-code/onepass";

import {
  dismissSeriesRecordingOptions,
  selectSeriesRecordingMode,
  selectSeriesRecordingOptions,
  selectSeriesRecordingOptionsTitle,
  selectSeriesRecordingUcid,
  showSeriesRecordingOptions
} from "./seriesRecordingOptionsSlice";
import type {
  RecordingOption,
  SeriesRecordingMode
} from "./seriesRecordingOptions.types";
import { RecordingOptionListType } from "./seriesRecordingOptions.types";
import {
  DynamicOptionsEngine,
  TOverlayOptions
} from "../../../model/dynamicOptions";
import { OnePassInteractor } from "../../../model/interactor/shared";
import { Locale, LocaleKeys } from "../../../model/locale";
import { showToastMessage } from "../../toastMessage";
import { recordingScheduled } from "../../utility/recordingEvents";
import type { UnityAppDispatch, UnityRootState } from "../../UnityAppStore";
import { Log } from "../../../model/logging/Log";
import { OperationalLogger } from "../../../model/logging";
import type { TAnalyticsFields } from "../../../model/types";
import { fetchMyHubSeriesDetail } from "../myHubSlice";
import { MY_HUB_SCREEN_NAME } from "../myHub.types";
import { logMyHubApiFailure } from "../myHubErrorMapper";
import {
  onePassActionErrorMapper,
  ONEPASS_ACTION_ERROR_CODE,
  SERIES_RECORDING_OPTIONS_LOAD_ERROR_CODE,
  seriesRecordingOptionsLoadErrorMapper
} from "../../../model/errorMapper/ScheduleActionErrorMappers";
import type { ActionErrorMapper } from "../../../model/errorMapper/ActionErrorMapper";

const TAG: string = "seriesRecordingOptionsThunks";

/**
 * Shared engine instance. The engine keeps its option/value maps in static
 * state, so load (seed) and save (map back) round-trip through the same data
 * regardless of which instance is used; a single instance keeps that explicit.
 */
const onePassEngine: DynamicOptionsEngine = new DynamicOptionsEngine();

/**
 * Toast text shown when a request cannot be processed, with the composite
 * code appended — R506 for a failed load, R325 for a failed save — applied
 * here so every caller of `loadSeriesRecordingOptions`/
 * `saveSeriesRecordingOptions` gets it, not just whichever screen happens to
 * dispatch them today.
 *
 * @param mapper - The mapper for the failing call's catalog code
 * @param error - The raw error that caused the failure, or omitted for a
 *                malformed-but-200-OK response with no error object
 */
function requestFailedToastMessage(
  mapper: ActionErrorMapper,
  error?: unknown
): string {
  return mapper.appendErrorCode(
    Locale.getString(LocaleKeys.REQUEST_FAILED_TOAST),
    error
  );
}

/**
 * Convert a OnePass `optionListType` (camelCase) into the lower snake_case
 * locale suffix used by the `series_recording_overlay_option_*` keys — mirrors
 * the engine's own header-key derivation so the row description resolves from
 * the same convention as the engine's header label.
 */
function localeSuffix(optionListType?: string): string {
  if (optionListType === undefined) {
    return "";
  }
  return optionListType.replaceAll(
    /[A-Z]/g,
    (letter: string): string => `_${letter.toLowerCase()}`
  );
}

/**
 * Strip unit suffixes from a picker option label so only the core value is
 * shown in the horizontal picker box.
 *
 * Rules (applied in order):
 *   1. Label starts with a digit → return the leading integer only
 *      ("10 minutes early" → "10", "1 recording" → "1").
 *   2. Non-numeric label → strip known trailing unit phrases
 *      ("All recordings" → "All", "On time" → "On time").
 *
 * The original label is kept in `horizontalOptions` for the engine save
 * round-trip; this derived value is display-only.
 */
function toPickerLabel(label: string): string {
  const numericMatch: RegExpMatchArray | null = label.match(/^(\d+)/);
  if (numericMatch !== null && numericMatch[1] !== undefined) {
    return numericMatch[1];
  }
  const unitSuffixes: string[] = [
    " recordings",
    " recording",
    " channels",
    " channel",
    " minutes early",
    " minute early",
    " minutes late",
    " minute late"
  ];
  const lower: string = label.toLowerCase();
  for (const suffix of unitSuffixes) {
    if (lower.endsWith(suffix)) {
      return label.slice(0, label.length - suffix.length);
    }
  }
  return label;
}

/**
 * Adapt one engine render row into the overlay's {@link RecordingOption}. The
 * label comes from the engine's localized header text; the description is
 * resolved from the matching `series_recording_overlay_option_*_description`
 * locale key. The horizontal options and index are retained so Save can map
 * back to the backend submission shape.
 */
function toRecordingOption(row: TOverlayOptions): RecordingOption | undefined {
  if (row.value.optionListType === undefined) {
    return undefined;
  }
  const optionListType = row.value.optionListType as RecordingOptionListType;
  const labels: string[] = row.horizontalSelectionoptions || [];
  const selectedIndex: number = row.initialHorizontalIndex || 0;
  const suffix: string = localeSuffix(optionListType);

  return {
    optionListType,
    label: row.text,
    description:
      suffix === ""
        ? ""
        : Locale.getString(
            `series_recording_overlay_option_${suffix}_description`
          ),
    value: labels[selectedIndex] || "",
    horizontalOptions: labels,
    displayOptions: labels.map(toPickerLabel),
    selectedIndex,
    disabled: row.disabled === true
  };
}

/**
 * The analytics field(s) one {@link RecordingOptionListType} corresponds to in
 * the logging spec's `createOnePass`/`modifyOnePass` field lists. `previous`
 * is omitted for the option types the spec has no `previous_*` field for
 * (`Channel` — there is no `previous_channels_option`).
 */
interface OnePassOptionAnalyticsKeys {
  current: keyof TAnalyticsFields;
  previous?: keyof TAnalyticsFields;
}

const ONE_PASS_OPTION_ANALYTICS_KEYS: Readonly<
  Partial<Record<RecordingOptionListType, OnePassOptionAnalyticsKeys>>
> = {
  [RecordingOptionListType.Channel]: { current: "channels_option" },
  [RecordingOptionListType.KeepAtMost]: {
    current: "keep_at_most_option",
    previous: "previous_keep_at_most_option"
  },
  [RecordingOptionListType.KeepUntil]: {
    current: "deletion_policy_option",
    previous: "previous_deletion_policy_option"
  },
  [RecordingOptionListType.StartRecording]: {
    current: "padding_duration_before_start_option",
    previous: "previous_padding_duration_before_start_option"
  },
  [RecordingOptionListType.StopRecording]: {
    current: "padding_duration_after_stop_option",
    previous: "previous_padding_duration_after_stop_option"
  },
  [RecordingOptionListType.RentOrBuy]: {
    current: "rent_or_buy_option",
    previous: "previous_rent_or_buy_option"
  },
  [RecordingOptionListType.Include]: {
    current: "include_option",
    previous: "previous_include_option"
  },
  [RecordingOptionListType.StartFrom]: {
    current: "start_from_option",
    previous: "previous_start_from_option"
  },
  [RecordingOptionListType.RecordType]: {
    current: "record_type_option",
    previous: "previous_record_type_option"
  },
  [RecordingOptionListType.VideoQuality]: {
    current: "get_in_hd_option",
    previous: "previous_get_in_hd_option"
  }
};

/**
 * The overlay's row values as they stood right after {@link loadSeriesRecordingOptions}
 * populated it — captured so Save can report what changed. `undefined` for a
 * "create" open, where there is no prior OnePass to diff against.
 */
let originalOnePassOptionValues:
  | ReadonlyMap<RecordingOptionListType, string>
  | undefined;

/**
 * Builds the OnePass option fields the spec lists (`keep_at_most_option`,
 * `get_in_hd_option`, `deletion_policy_option`, …) from the overlay's current
 * row selections, so `createOnePass`/`modifyOnePass` report what the user
 * actually chose rather than only the series identity. When `previousValues`
 * is supplied (a "modify" save) and a row's value actually changed, also adds
 * the matching `previous_*_option` field with what it was before.
 */
function buildOnePassOptionAnalyticsFields(
  options: RecordingOption[],
  previousValues?: ReadonlyMap<RecordingOptionListType, string>
): Partial<TAnalyticsFields> {
  const fields: Partial<TAnalyticsFields> = {};
  options.forEach((option: RecordingOption): void => {
    const keys: OnePassOptionAnalyticsKeys | undefined =
      ONE_PASS_OPTION_ANALYTICS_KEYS[option.optionListType];
    if (keys === undefined) {
      return;
    }
    if (option.value !== "") {
      fields[keys.current] = option.value;
    }
    if (keys.previous === undefined || previousValues === undefined) {
      return;
    }
    const previousValue: string | undefined = previousValues.get(
      option.optionListType
    );
    if (
      previousValue !== undefined &&
      previousValue !== "" &&
      previousValue !== option.value
    ) {
      fields[keys.previous] = previousValue;
    }
  });
  return fields;
}

/**
 * Rebuild the engine render rows and the per-row selected indices from the
 * overlay's current {@link RecordingOption}s, so the engine can produce the
 * backend submission values.
 */
function toOverlayRows(options: RecordingOption[]): {
  rows: TOverlayOptions[];
  positions: number[];
} {
  const rows: TOverlayOptions[] = options.map(
    (option: RecordingOption): TOverlayOptions => ({
      text: option.label,
      value: {
        optionListType: option.optionListType as OptionType
      },
      horizontalSelectionoptions: option.horizontalOptions
    })
  );
  const positions: number[] = options.map(
    (option: RecordingOption): number => option.selectedIndex || 0
  );
  return { rows, positions };
}

/**
 * Open the Series Recording Options overlay: fetch schedule + options in
 * parallel, seed the engine, and populate the slice with pre-filled rows.
 *
 * Works for both flows — the `options`/`schedule` endpoints are keyed only by
 * the series `ucid`, so a not-yet-recorded series returns its default values
 * just as an existing OnePass returns its current ones. `mode` (default
 * "modify") is carried through to the slice so Save knows whether to POST
 * (create) or PUT (update); it does not change the load itself.
 */
export const loadSeriesRecordingOptions = createAsyncThunk<
  void,
  {
    ucid: string;
    title: string;
    mode?: SeriesRecordingMode;
    /** Caller's screen name/id, for uiError attribution — omitted callers log without screen context. */
    screen?: string;
    screenId?: string;
  },
  { state: UnityRootState; rejectValue: string }
>(
  "seriesRecordingOptions/load",
  async (
    { ucid, title, mode, screen, screenId },
    { dispatch, rejectWithValue }
  ) => {
    try {
      Log.i(
        TAG,
        "loadSeriesRecordingOptions() - fetching for ucid " +
          ucid +
          " mode " +
          (mode === undefined ? "modify" : mode)
      );
      const interactor: OnePassInteractor = new OnePassInteractor();
      const [schedule, options] = await Promise.all([
        interactor.fetchSchedule(ucid),
        interactor.fetchOptions(ucid)
      ]);

      const values = schedule.onePassValues;
      if (options === undefined || values === undefined) {
        Log.e(TAG, "loadSeriesRecordingOptions() - missing options or values");
        logMyHubApiFailure(
          new Error("Missing OnePass options or values"),
          SERIES_RECORDING_OPTIONS_LOAD_ERROR_CODE,
          "loadSeriesRecordingOptions"
        );
        dispatch(
          showToastMessage(
            requestFailedToastMessage(seriesRecordingOptionsLoadErrorMapper)
          )
        );
        return rejectWithValue("Missing OnePass options or values");
      }

      onePassEngine.setOnePassOptions(options, values);
      const rows: TOverlayOptions[] =
        onePassEngine.getInitialValuesOfOnePassOptions();
      const initialOptions: RecordingOption[] = rows
        .map(toRecordingOption)
        .filter((o): o is RecordingOption => o !== undefined);

      // Snapshot the pre-edit values so Save can report `previous_*_option`
      // fields — only meaningful for a "modify" open; "create" has no prior
      // OnePass to diff against.
      originalOnePassOptionValues =
        mode === "create"
          ? undefined
          : new Map(
              initialOptions.map(
                (
                  option: RecordingOption
                ): [RecordingOptionListType, string] => [
                  option.optionListType,
                  option.value
                ]
              )
            );

      dispatch(
        showSeriesRecordingOptions({
          title,
          ucid,
          mode,
          options: initialOptions,
          saveButtonLabel: Locale.getString("series_recording_overlay_save"),
          cancelButtonLabel: Locale.getString("series_recording_overlay_cancel")
        })
      );
      return undefined;
    } catch (error: unknown) {
      // Schedule/options fetch failed — toast instead of opening the overlay.
      Log.e(TAG, "loadSeriesRecordingOptions()", error);
      logMyHubApiFailure(
        error,
        SERIES_RECORDING_OPTIONS_LOAD_ERROR_CODE,
        "loadSeriesRecordingOptions"
      );
      dispatch(
        showToastMessage(
          requestFailedToastMessage(
            seriesRecordingOptionsLoadErrorMapper,
            error
          )
        )
      );
      if (screen !== undefined || screenId !== undefined) {
        OperationalLogger.logUiError({
          errorCode:
            seriesRecordingOptionsLoadErrorMapper.toCompositeCode(error),
          errorMessage: OperationalLogger.describeErrorMessage(error),
          screen,
          screenId
        });
      }
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to load OnePass options"
      );
    }
  }
);

/**
 * Persist the user's selections: map the current rows back to the backend
 * submission shape via the engine, then POST (create) or PUT (modify) based
 * on the overlay's mode, and dismiss the overlay with reason "save".
 */
export const saveSeriesRecordingOptions = createAsyncThunk<
  void,
  void,
  { state: UnityRootState; dispatch: UnityAppDispatch; rejectValue: string }
>(
  "seriesRecordingOptions/save",
  async (_arg: void, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const ucid: string = selectSeriesRecordingUcid(state);
    const mode: SeriesRecordingMode = selectSeriesRecordingMode(state);
    const options: RecordingOption[] = selectSeriesRecordingOptions(state);
    const title: string = selectSeriesRecordingOptionsTitle(state);

    try {
      const { rows, positions } = toOverlayRows(options);
      const onePassValues = onePassEngine.getUserSelectedOnePassValues(
        rows,
        positions
      );

      if (ucid !== "") {
        const interactor: OnePassInteractor = new OnePassInteractor();
        const optionFields: Partial<TAnalyticsFields> =
          buildOnePassOptionAnalyticsFields(
            options,
            mode === "create" ? undefined : originalOnePassOptionValues
          );
        // Fires `createOnePass`/`modifyOnePass` on success — see
        // OnePassInteractor.createOnePass()/updateOnePass().
        if (mode === "create") {
          await interactor.createOnePass(ucid, onePassValues, undefined, {
            collection_title: title,
            screen: MY_HUB_SCREEN_NAME,
            ...optionFields
          });
        } else {
          await interactor.updateOnePass(ucid, onePassValues, undefined, {
            collection_title: title,
            screen: MY_HUB_SCREEN_NAME,
            ...optionFields
          });
        }
        if (state.myHub.series.unifiedCollectionId === ucid) {
          dispatch(fetchMyHubSeriesDetail(ucid));
        }
        dispatch(recordingScheduled({ unifiedCollectionId: ucid }));
      }

      originalOnePassOptionValues = undefined;
      onePassEngine.clearOnePassOptions();
      dispatch(dismissSeriesRecordingOptions("save"));
      return undefined;
    } catch (error: unknown) {
      // Save failed — dismiss the overlay and toast.
      Log.e(TAG, "saveSeriesRecordingOptions()", error);
      logMyHubApiFailure(
        error,
        ONEPASS_ACTION_ERROR_CODE,
        "saveSeriesRecordingOptions"
      );
      originalOnePassOptionValues = undefined;
      onePassEngine.clearOnePassOptions();
      dispatch(dismissSeriesRecordingOptions("error"));
      dispatch(
        showToastMessage(
          requestFailedToastMessage(onePassActionErrorMapper, error)
        )
      );
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to save OnePass"
      );
    }
  }
);
